import React from 'react';
import { Calendar, Trash2, Edit3, Plus, Minus, Repeat } from 'lucide-react';

const TaskCard = ({ task, onEdit, onDelete, onUpdate }) => {
    const getPriorityColor = (priority) => {
        const colors = {
            high: 'text-red-500',
            medium: 'text-yellow-500',
            low: 'text-green-500'
        };
        return colors[priority] || colors.low;
    };

    const getStatusColor = (status) => {
        const colors = {
            'todo': 'bg-gray-500/20 text-gray-300',
            'in-progress': 'bg-blue-500/20 text-blue-300',
            'done': 'bg-green-500/20 text-green-300'
        };
        return colors[status] || colors.todo;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No due date';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
        if (diffDays === 0) return 'Due today';
        if (diffDays === 1) return 'Due tomorrow';
        return `Due in ${diffDays} days`;
    };

    const toggleSubtask = (subtaskIndex) => {
        const updatedSubtasks = [...(task.subtasks || [])];
        updatedSubtasks[subtaskIndex] = {
            ...updatedSubtasks[subtaskIndex],
            completed: !updatedSubtasks[subtaskIndex].completed
        };
        onUpdate(task.id, { ...task, subtasks: updatedSubtasks });
    };

    const completedSubtasks = task.subtasks?.filter(st => st.completed).length || 0;
    const totalSubtasks = task.subtasks?.length || 0;

    return (
        <div className="glass-card p-5 slide-in group">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-accent transition-colors">
                        {task.title}
                    </h3>
                    <div className="flex items-center gap-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status?.replace('-', ' ')}
                        </span>
                        <span className={`font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority} priority
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={() => onEdit(task)}
                        className="p-2 hover:bg-white/5 rounded transition-colors"
                        title="Edit task"
                    >
                        <Edit3 size={16} className="text-white/70 hover:text-accent" />
                    </button>
                    <button
                        onClick={() => onDelete(task.id)}
                        className="p-2 hover:bg-white/5 rounded transition-colors"
                        title="Delete task"
                    >
                        <Trash2 size={16} className="text-white/70 hover:text-red-500" />
                    </button>
                </div>
            </div>

            {/* Description */}
            {task.description && (
                <p className="text-white/70 text-sm mb-4 line-clamp-2">
                    {task.description}
                </p>
            )}

            {/* Task Type Specific Controls */}
            {task.type === 'habit' && (
                <div className="flex items-center justify-between mb-4 bg-white/5 p-3 rounded-lg">
                    <span className="text-sm text-white/70">Habit Streak</span>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                // Handle habit decrement (future)
                            }}
                            className="p-1 hover:bg-red-500/20 text-white/50 hover:text-red-400 rounded transition-colors"
                        >
                            <Minus size={18} />
                        </button>
                        <span className="font-mono text-lg font-bold text-accent">{task.streak || 0}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onUpdate(task.id, { ...task, status: 'completed' }); // Trigger completion logic
                            }}
                            className="p-1 hover:bg-green-500/20 text-white/50 hover:text-green-400 rounded transition-colors"
                        >
                            <Plus size={18} />
                        </button>
                    </div>
                </div>
            )}

            {task.type === 'daily' && (
                <div className="flex items-center gap-2 mb-4 text-sm text-white/60">
                    <Repeat size={14} className="text-accent" />
                    <span>Repeats daily</span>
                </div>
            )}

            {/* Subtasks Progress (Only for Todos or if subtasks exist) */}
            {(task.type === 'todo' || (task.subtasks && task.subtasks.length > 0)) && totalSubtasks > 0 && (
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/50">Subtasks</span>
                        <span className="text-xs text-white/50">
                            {completedSubtasks}/{totalSubtasks}
                        </span>
                    </div>
                    <div className="flex gap-1 mb-2">
                        {task.subtasks.map((_, index) => (
                            <div
                                key={index}
                                className={`progress-dot ${task.subtasks[index].completed ? 'completed' : ''}`}
                            />
                        ))}
                    </div>
                    {/* Subtask checkboxes */}
                    <div className="space-y-1">
                        {task.subtasks.slice(0, 3).map((subtask, index) => (
                            <label
                                key={index}
                                className="flex items-center gap-2 text-sm text-white/60 hover:text-white/80 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={subtask.completed}
                                    onChange={() => toggleSubtask(index)}
                                    className="rounded border-white/20 bg-white/5 text-accent focus:ring-accent"
                                />
                                <span className={subtask.completed ? 'line-through opacity-50' : ''}>
                                    {subtask.title}
                                </span>
                            </label>
                        ))}
                        {task.subtasks.length > 3 && (
                            <button
                                onClick={() => onEdit(task)}
                                className="text-xs text-accent hover:text-accent/80 ml-6"
                            >
                                +{task.subtasks.length - 3} more
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                {/* Due Date */}
                <div className="flex items-center gap-2 text-sm text-white/50">
                    <Calendar size={14} />
                    <span>{formatDate(task.dueDate)}</span>
                </div>

                {/* Tags */}
                {task.tags && task.tags.length > 0 && (
                    <div className="flex items-center gap-1">
                        {task.tags.slice(0, 2).map((tag, index) => (
                            <span key={index} className="tag-chip text-xs">
                                {tag}
                            </span>
                        ))}
                        {task.tags.length > 2 && (
                            <span className="text-xs text-white/40">+{task.tags.length - 2}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskCard;
